import React from 'react';
import { Modal, Form, Input, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { updateUserPassword } from '../../../api/auth';
import { useTranslation } from 'react-i18next';
import type { User } from '../../../types/users';

interface ChangePasswordModalProps {
  visible: boolean;
  user: User;
  onCancel: () => void;
  onSuccess: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  visible,
  user,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation(['users', 'common']);
  const [form] = Form.useForm();

  const updateMutation = useMutation({
    mutationFn: (data: { password: string }) => updateUserPassword(user.id, data.password),
    onSuccess: () => {
      message.success(t('users:messages.passwordUpdateSuccess'));
      form.resetFields();
      onSuccess();
    },
    onError: () => message.error(t('users:messages.passwordUpdateError')),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      updateMutation.mutate(values);
    } catch (error) {
      // Form validation failed
    }
  };

  return (
    <Modal
      title={t('users:changePassword')}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={updateMutation.isPending}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="password"
          label={t('users:fields.newPassword')}
          rules={[
            { required: true, message: t('common:validation.required') },
            { min: 6, message: t('users:validation.passwordMinLength') },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label={t('users:fields.confirmPassword')}
          dependencies={['password']}
          rules={[
            { required: true, message: t('common:validation.required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t('common:validation.passwordMatch')));
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal; 